//#region IMPORTS
import {
  createInstoreThemeAsync,
  searchInstoreThemesAsync,
  clearSearchedInstoreThemes,
} from "../../features/instoreThemes/instoreThemesSlice";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { useEffect, useState } from "react";
import { InstoreTheme } from "../../models/instoreTheme";
import MainFormComboBoxServerQuery from "../_layout/MainFormComboBoxServerQuery";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  addThemeRowHandler: (theme: InstoreTheme) => void;
  usedThemes?: string[];
  cancel: () => void;
}
//#endregion

export default function InstoreOrderLineForm({
  addThemeRowHandler,
  usedThemes = [],
  cancel,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading, instoreThemes_Loaded, searchedThemes } = useAppSelector(
    ({ instoreThemes }) => instoreThemes
  );

  // Local state
  const [createNew, setCreateNew] = useState<boolean>(false);
  const [availableThemeNames, setAvailableThemeNames] = useState<string[]>([]);

  // UseForm
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({ mode: "all", defaultValues: { name: "", newTheme: false } });
  //#endregion

  //#region LOGIC
  // Available theme names
  useEffect(() => {
    const usedThemesSet = new Set(usedThemes.map((t) => t.toLowerCase()));

    const availableUnusedThemeNames = searchedThemes
      .map((theme) => theme.name)
      .filter((name): name is string => Boolean(name)) // remove null/undefined
      .filter((name) => !usedThemesSet.has(name.toLowerCase()));

    setAvailableThemeNames(availableUnusedThemeNames);
  }, [searchedThemes, usedThemes]);

  // Submit new or used theme
  async function submitForm({ name }: FieldValues) {
    let theme: InstoreTheme | undefined = undefined;

    if (createNew) {
      theme = await dispatch(
        createInstoreThemeAsync({ values: { ...{ name } } })
      ).unwrap();
      addThemeRowHandler(theme);
    } else {
      theme = searchedThemes.find(
        (t) => t.name.toLowerCase() === name.toLowerCase()
      );
      theme && addThemeRowHandler(theme);
      setAvailableThemeNames([]);
      dispatch(clearSearchedInstoreThemes());
    }
  }
  //#endregion

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          <header>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Add theme to the list
            </h2>
          </header>

          <section className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* theme selector */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="theme"
                className={
                  errors.name
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.name
                  ? errors?.name?.message
                  : createNew
                  ? "Create and add a new theme"
                  : "Select a existing theme"}
              </label>
              <div className="relative mt-2">
                <Controller
                  control={control}
                  name="name"
                  rules={{ required: "The theme field is required" }}
                  render={({ field }) =>
                    createNew ? (
                      <input
                        className="block w-full rounded-md px-3 py-1.5 border border-gray-300 text-gray-900 bg-white focus:border-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-100"
                        placeholder="enter new theme name"
                        onChange={(e) => field.onChange(e.target.value)}
                        disabled={loading || isSubmitting}
                      />
                    ) : (
                      <MainFormComboBoxServerQuery
                        onCreateNewClickHandler={() => {
                          setCreateNew(true);
                          setValue("newTheme", true);
                        }}
                        onChangeHandler={(value: string) => {
                          field.onChange(value);
                        }}
                        onSearchHandler={async (value: string) => {
                          !!value
                            ? await dispatch(searchInstoreThemesAsync(value))
                            : setAvailableThemeNames([]);
                        }}
                        filteredItems={availableThemeNames}
                        placeHolder="search theme"
                        defaultItem={field.value}
                        showError={!!errors.name}
                        disabled={
                          loading || isSubmitting || !instoreThemes_Loaded
                        }
                      />
                    )
                  }
                />
              </div>
            </fieldset>

            {/* Actions */}
            <div className="sm:col-span-3 mt-6 flex items-center justify-end gap-x-6">
              <button
                onClick={cancel}
                type="button"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!isValid}
                className={classNames(
                  isValid
                    ? "bg-emerald-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 hover:bg-emerald-500 focus-visible:outline"
                    : "bg-gray-600 opacity-40",
                  "rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm"
                )}
              >
                {isSubmitting
                  ? "Adding..."
                  : createNew
                  ? "Create and add to list"
                  : "Add to list"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </form>
  );
}
